/**
 * TradeDoubler (Grow) affiliate tracking for the customer app.
 *
 * The marketing site loads the same tr_sdk.js as a landing-page tag, which
 * grabs the tduid off an affiliate click URL and stores it in a first-party
 * cookie on .mediloop.ai. Because that cookie sits on the parent domain it
 * is readable here on my.mediloop.ai, so all this file has to do is load the
 * SDK again and report the sale once Stripe has confirmed payment.
 *
 * Env vars (all public, from Grow → Program settings → Tracking set up, step 3):
 *   VITE_TRADEDOUBLER_ORG_ID      – organisation id
 *   VITE_TRADEDOUBLER_PROGRAM_ID  – program id
 *   VITE_TRADEDOUBLER_EVENT_ID    – the sale event id
 * Leave them unset in dev/staging and everything here is a no-op.
 */

import { isBlockedTrackingHost } from './fbpixel';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    tdconv?: ((...args: any[]) => void) & { q?: any[]; l?: number };
    TDConversionObject?: string;
  }
}

const ORG_ID = import.meta.env.VITE_TRADEDOUBLER_ORG_ID as string | undefined;
const PROGRAM_ID = import.meta.env.VITE_TRADEDOUBLER_PROGRAM_ID as string | undefined;
const EVENT_ID = import.meta.env.VITE_TRADEDOUBLER_EVENT_ID as string | undefined;

function isConfigured(): boolean {
  return Boolean(ORG_ID && PROGRAM_ID && EVENT_ID);
}

/**
 * Load tr_sdk.js and set up the `tdconv` queue. Idempotent, call once from
 * main.tsx next to the other pixel inits. Nothing fires until
 * trackTradedoublerSale() is called.
 */
export function initTradedoubler(): void {
  if (!isConfigured()) return;
  if (typeof window === 'undefined') return;
  if (isBlockedTrackingHost()) return;
  if (typeof window.tdconv === 'function') return;

  // TradeDoubler's snippet, written out longhand. Calls made before the SDK
  // arrives are queued on tdconv.q and replayed once it loads.
  const w = window as any;
  w.TDConversionObject = 'tdconv';
  const q: any = function (...args: any[]) {
    (q.q = q.q || []).push(args);
  };
  q.l = Date.now();
  w.tdconv = q;

  const script = document.createElement('script');
  script.async = true;
  script.src =
    `https://svht.tradedoubler.com/tr_sdk.js?org=${encodeURIComponent(ORG_ID!)}` +
    `&prog=${encodeURIComponent(PROGRAM_ID!)}&dr=true&rand=${Math.random()}`;
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

interface SaleParams {
  /** Total order value in major units (e.g. 129 for $129). */
  value: number;
  currency?: string;
  /** Unique per transaction. We use the Stripe checkout session id, same as every other Purchase leg. */
  transactionId: string;
  /** Promo / voucher code the customer used, if any. */
  voucher?: string;
}

/**
 * Report a sale to TradeDoubler. Guarded on sessionStorage so a refresh of
 * the confirmation page doesn't report the same order twice, and skipped for
 * Stripe test-mode sessions like the Meta and Reddit legs.
 */
export function trackTradedoublerSale({
  value,
  currency = 'USD',
  transactionId,
  voucher,
}: SaleParams): void {
  if (!isConfigured()) return;
  if (transactionId?.startsWith('cs_test_')) return;
  if (typeof window === 'undefined' || typeof window.tdconv !== 'function') return;

  const storageKey = `td_sale_tracked_${transactionId}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // sessionStorage unavailable (private browsing), carry on
  }

  window.tdconv('init', ORG_ID, { element: 'iframe' });
  window.tdconv('track', 'sale', {
    transactionId,
    ordervalue: value,
    voucher: voucher ?? '',
    currency,
    event: Number(EVENT_ID),
  });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // ignore
  }
}
